// ============================================================
// SocialService — socket.io wrapper for the Social Hub
// ============================================================

import type {
  SocialListMode,
  SocialListResult,
  SocialRelation,
  SocialUserSummary,
  SocialSearchResult,
  SocialUpdate,
} from '@roe/shared';
import { SocketService } from './SocketService.js';

/** TTL for the friends/pending caches — refreshed on social:update events,
 *  this is the safety-net fallback when no event has fired recently. */
const CACHE_TTL_MS = 30_000;

export class SocialService {
  private static instance: SocialService;
  private wired = false;
  private retryScheduled = false;
  private updateListeners: Set<(u: SocialUpdate) => void> = new Set();
  private pendingCount = 0;
  /** Cache of accepted friends. Used by the chat context-menu relation lookup
   *  and the Tab-autocomplete. Refreshed on social:update { friends-changed }
   *  or after the TTL expires — whichever fires first. */
  private friendsCache: SocialUserSummary[] = [];
  private friendsCacheAt = 0;
  private friendsRefreshInflight = false;
  /** Cache of incoming pending friend requests — used to distinguish
   *  `pending_received` from `none` in the context menu. */
  private pendingCache: SocialUserSummary[] = [];
  private pendingCacheAt = 0;
  private pendingRefreshInflight = false;

  static getInstance(): SocialService {
    if (!SocialService.instance) SocialService.instance = new SocialService();
    return SocialService.instance;
  }

  ensureWired(): void {
    if (this.wired) return;
    const sock = SocketService.getInstance().getSocket();
    if (!sock) {
      // Socket may not be connected yet (user just logged in, connect() in flight).
      // Self-poll so the pending-count hydration lands even if ensureWired() was
      // called before the socket came up. Cheap; stops as soon as wired flips true.
      if (!this.retryScheduled) {
        this.retryScheduled = true;
        setTimeout(() => { this.retryScheduled = false; this.ensureWired(); }, 800);
      }
      return;
    }
    sock.on('social:update', (u: SocialUpdate) => {
      if (u.kind === 'pending-changed' && typeof u.pendingCount === 'number') {
        this.pendingCount = u.pendingCount;
      }
      // Invalidate caches on any mutation so the next getFriendsCache /
      // getRelation call fetches fresh data.
      if (u.kind === 'friends-changed') this.friendsCacheAt = 0;
      if (u.kind === 'pending-changed') this.pendingCacheAt = 0;
      for (const fn of this.updateListeners) {
        try { fn(u); } catch (e) { console.error('[social listener]', e); }
      }
    });
    sock.on('social:error', (err: { code: string; message: string }) => {
      console.warn('[social error]', err);
    });
    this.wired = true;
  }

  /** Current pending-friend-request count for the logged-in user. Kept in sync
   *  via `social:update` events pushed by the server; survives scene reloads
   *  because the service is a singleton. */
  getPendingCount(): number {
    return this.pendingCount;
  }

  onUpdate(fn: (u: SocialUpdate) => void): () => void {
    this.updateListeners.add(fn);
    return () => this.updateListeners.delete(fn);
  }

  async list(mode: SocialListMode, page = 0): Promise<SocialListResult | null> {
    const sock = SocketService.getInstance().getSocket();
    if (!sock) return null;
    return new Promise((resolve) => {
      sock.emit('social:list', { mode, page }, (res: SocialListResult | { error: string }) => {
        if ('error' in res) resolve(null);
        else resolve(res);
      });
    });
  }

  async search(query: string): Promise<SocialSearchResult | null> {
    const sock = SocketService.getInstance().getSocket();
    if (!sock) return null;
    return new Promise((resolve) => {
      sock.emit('social:search', { query }, (res: SocialSearchResult | { error: string }) => {
        if ('error' in res) resolve(null);
        else resolve(res);
      });
    });
  }

  sendFriendRequest(userId: string):   void { SocketService.getInstance().getSocket()?.emit('social:friend-request', { userId }); }
  acceptFriend(userId: string):        void { SocketService.getInstance().getSocket()?.emit('social:friend-accept',  { userId }); }
  rejectFriend(userId: string):        void { SocketService.getInstance().getSocket()?.emit('social:friend-reject',  { userId }); }
  /** Cancel a friend request I sent (distinct from reject which is recipient-side). */
  cancelFriendRequest(userId: string): void { SocketService.getInstance().getSocket()?.emit('social:friend-cancel',  { userId }); }
  removeFriend(userId: string):        void { SocketService.getInstance().getSocket()?.emit('social:friend-remove',  { userId }); }
  block(userId: string):               void { SocketService.getInstance().getSocket()?.emit('social:block',          { userId }); }
  unblock(userId: string):             void { SocketService.getInstance().getSocket()?.emit('social:unblock',        { userId }); }

  /** Synchronous read of the cached friends list. Triggers a background
   *  refresh if the cache is stale (never fetched or past TTL). Callers get
   *  the last-known list immediately — context-menu rendering + Tab
   *  autocomplete don't need freshness-of-the-second. */
  getFriendsCache(): readonly SocialUserSummary[] {
    if (Date.now() - this.friendsCacheAt > CACHE_TTL_MS) this.refreshFriends();
    return this.friendsCache;
  }

  /** Infer relation from the cached friends + pending lists. Returns
   *  undefined for unknown users — callers default to 'none'. See plan
   *  Task 4 Key Decisions for the precedence rules and limitations. */
  getRelation(userId: string): SocialRelation | undefined {
    // Ensure both caches are populated (fire-and-forget if missing).
    if (Date.now() - this.friendsCacheAt > CACHE_TTL_MS) this.refreshFriends();
    if (Date.now() - this.pendingCacheAt > CACHE_TTL_MS) this.refreshPending();
    if (this.friendsCache.some((u) => u.id === userId)) return 'friend';
    if (this.pendingCache.some((u) => u.id === userId)) return 'pending_received';
    return undefined;
  }

  private refreshFriends(): void {
    if (this.friendsRefreshInflight) return;
    this.friendsRefreshInflight = true;
    void this.list('friends').then((res) => {
      if (res && Array.isArray(res.entries)) {
        this.friendsCache = res.entries;
        this.friendsCacheAt = Date.now();
      }
    }).finally(() => { this.friendsRefreshInflight = false; });
  }

  private refreshPending(): void {
    if (this.pendingRefreshInflight) return;
    this.pendingRefreshInflight = true;
    void this.list('pending').then((res) => {
      if (res && Array.isArray(res.entries)) {
        this.pendingCache = res.entries;
        this.pendingCacheAt = Date.now();
      }
    }).finally(() => { this.pendingRefreshInflight = false; });
  }
}
